import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useRoomChatSocket } from '../contexts/RoomChatSocketContext';
import ChatStatusIndicator from './ChatStatusIndicator';
import '../styles/WaitingRoom.css';
import logger from '../utils/logger';

const WaitingRoom = ({ gameId, roomName, onGameReady, onLeave, addGlobalNotification }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const { user, token } = useAuth();
  const { connected, chatStatus, sendMessage, addMessageHandler, connectToRoom, disconnect } = useRoomChatSocket();

  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [roomInfo, setRoomInfo] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const messagesEndRef = useRef(null);
  const gameStartTriggered = useRef(false);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle incoming WebSocket messages
  const handleSocketMessage = useCallback((data) => {
    if (data.type === 'connected') {
    } else if (data.type === 'message') {
      setMessages(prev => [...prev, {
        user_name: data.user_name,
        message: data.message,
        timestamp: data.timestamp,
        isMe: data.user_id === user?.user_id
      }]);
    } else if (data.type === 'user_joined') {
      setMessages(prev => [...prev, {
        system: true,
        message: `${data.user_name} joined the room`
      }]);
    } else if (data.type === 'player_joined') {
      // Player joined the game - show notification and update status
      setMessages(prev => [...prev, {
        system: true,
        message: `🎉 ${data.user_name} joined as ${data.color}!`
      }]);
      
      // Add global notification so it persists
      if (addGlobalNotification) {
        addGlobalNotification(
          'room_joined',
          gameId,
          roomName,
          `${data.user_name} joined your room!`
        );
      }
    } else if (data.type === 'user_left') {
      setMessages(prev => [...prev, {
        system: true,
        message: `${data.user_name} left the room`
      }]);
    }
  }, [user?.user_id, addGlobalNotification, gameId, roomName]);

  // Connect to room WebSocket and register message handler
  // Using refs for stable function references, so only gameId changes trigger reconnection
  useEffect(() => {
    if (!gameId) return;
    
    // Connect to the room
    connectToRoom(gameId);
    
    // Register message handler
    const unregisterHandler = addMessageHandler(handleSocketMessage);
    
    // Cleanup on unmount
    return () => {
      unregisterHandler();
      disconnect();
    };
  }, [gameId, connectToRoom, disconnect, addMessageHandler, handleSocketMessage]);

  // Fetch room info and enter room
  useEffect(() => {
    const enterAndFetchRoom = async () => {
      if (!token) {
        logger.error('❌ CRITICAL: No authentication token available!');
        return;
      }
      
      try {
        // First, call enter endpoint to re-assign player if needed
        const enterResponse = await fetch(`${API_URL}/api/multiplayer/rooms/${gameId}/enter`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (enterResponse.ok) {
          const enterData = await enterResponse.json();
        } else {
        }
      } catch (e) {
        logger.warn('Room enter request failed:', e.message);
      }
      
      // Then fetch room info
      try {
        const fetchUrl = `${API_URL}/api/multiplayer/rooms/${gameId}`;
        const response = await fetch(fetchUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        // Read response body ONCE
        const rawText = await response.text();
        let data = null;
        
        if (rawText) {
          try {
            data = JSON.parse(rawText);
          } catch (e) {
            logger.warn('⚠️ Room info response is not valid JSON:', rawText);
          }
        }
        
        if (!response.ok) {
          logger.error(`❌ Room info fetch failed: ${response.status} ${response.statusText}`, data);
          
          if (response.status === 401) {
            logger.error('⚠️ Room info unauthorized (401). Token may be invalid or expired.');
            logger.error('⚠️ Token being used:', token ? `${token.substring(0, 30)}...` : 'NONE');
          }
          return;
        }
        
        if (data) {
          setRoomInfo(data);
          
          // Check if both players are present
          if (data.white_player_id && data.black_player_id && data.status === 'active') {
            setIsReady(true);
            
            // Auto-start game after 2 seconds (only once)
            if (!gameStartTriggered.current) {
              gameStartTriggered.current = true;
              setTimeout(() => {
                onGameReady();
              }, 2000);
            }
          }
        }
      } catch (error) {
        logger.error('❌ Exception while fetching room info:', error);
      }
    };

    enterAndFetchRoom();
    
    // Poll for updates every 2 seconds (just fetch, don't re-enter)
    const pollRoomInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/multiplayer/rooms/${gameId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setRoomInfo(data);
          
          // Check if game is ready
          const hasWhite = !!data.white_player_id;
          const hasBlack = !!data.black_player_id;
          if (hasWhite && hasBlack && data.status === 'active' && !gameStartTriggered.current) {
            gameStartTriggered.current = true;
            setTimeout(() => onGameReady(), 2000);
          }
        }
      } catch (e) {
        logger.error('Poll error:', e);
      }
    };
    
    const interval = setInterval(pollRoomInfo, 2000);
    
    return () => clearInterval(interval);
  }, [gameId, token, API_URL, onGameReady]);

  const handleSendMessage = () => {
    if (!messageInput.trim() || !connected) return;

    sendMessage({
      type: 'message',
      message: messageInput.trim()
    });

    setMessageInput('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const playerCount = roomInfo ? 
    (roomInfo.white_player_id ? 1 : 0) + (roomInfo.black_player_id ? 1 : 0) : 0;

  return (
    <div className="waiting-room-overlay">
      <div className="waiting-room-modal">
        {/* Header */}
        <div className="waiting-room-header">
          <div className="room-title">
            <h2>{roomName || 'Game Room'}</h2>
            <span className="room-code">{roomInfo?.room_code}</span>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            {roomInfo && (
              <span style={{
                background: 'rgba(255, 255, 255, 0.2)',
                padding: '6px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {roomInfo.white_player_id === user.user_id ? '⚪ YOU: WHITE' : 
                 roomInfo.black_player_id === user.user_id ? '⚫ YOU: BLACK' : 
                 '⏳ WAITING'}
              </span>
            )}
            <button className="close-btn" onClick={onLeave}>×</button>
          </div>
        </div>

        {/* Status */}
        <div className="waiting-room-status">
          {isReady ? (
            <div className="status-ready">
              <div className="status-icon">✅</div>
              <h3>Game Starting!</h3>
              <p>Both players are ready. Game will start in a moment...</p>
            </div>
          ) : (
            <div className="status-waiting">
              <div className="status-icon">⏳</div>
              <h3>Waiting for Players</h3>
              <p>{playerCount}/2 players in room</p>
              {roomInfo?.room_code && (
                <div className="share-code">
                  <p>Share this code with your opponent:</p>
                  <div className="code-display">{roomInfo.room_code}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chat Section */}
        <div className="waiting-room-chat">
          <div className="chat-header">
            <span>💬 Room Chat</span>
            <ChatStatusIndicator status={chatStatus} />
          </div>
          
          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="chat-empty">
                <p>No messages yet. Start chatting while you wait!</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div 
                  key={`msg-${msg.timestamp || ''}-${msg.user_name || 'sys'}`} 
                  className={`chat-message ${msg.system ? 'system' : ''} ${msg.isMe ? 'me' : ''}`}
                >
                  {msg.system ? (
                    <span className="system-text">{msg.message}</span>
                  ) : (
                    <>
                      <span className="message-user">{msg.user_name}:</span>
                      <span className="message-text">{msg.message}</span>
                    </>
                  )}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <input
              type="text"
              className="chat-input"
              placeholder={connected ? "Type a message..." : "Connecting..."}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={!connected}
            />
            <button 
              className="chat-send-btn"
              onClick={handleSendMessage}
              disabled={!messageInput.trim() || !connected}
            >
              Send
            </button>
          </div>
        </div>

        {/* Leave Button */}
        <div className="waiting-room-footer">
          <button className="leave-room-btn" onClick={onLeave}>
            Leave Room
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaitingRoom;
